import {Injectable} from '@angular/core';
import {StatusEventDetails} from '@models/machine/status-event-signal/status-event-details';
import {StatusEventSignalParsingException} from '@models/machine/status-event-signal/status-event-signal-parsing-exception';
import {StatusEventSignalColumnKind} from '@constants/machine/status-event-signal/status-event-signal-column-type.enum';
import {
  STATUS_EVENT_SIGNAL_LANGUAGE_COLUMN_PREFIX
} from '@constants/machine/status-event-signal/status-event-signal.constants';
import {TranslateService} from '@ngx-translate/core';
import {
  StatusEventExceptionTypes
} from '@constants/machine/status-event-signal/status-event-exception-types.enum';

@Injectable({
  providedIn: 'root'
})
export class StatusEventSignalContentPreviewService {

  private TABLE_TAG_START: string = '<table class="table table-striped table-responsive">';
  private TABLE_TAG_END: string = '</table>';
  private TABLE_HEADER_START: string = '<th>';
  private TABLE_HEADER_END: string = '</th>';
  private TABLE_ROW_START: string = '<tr>';
  private TABLE_ROW_END: string = '</tr>';
  private TABLE_CELL_START: string = '<td>';
  private TABLE_CELL_END: string = '</td>';

  constructor(private translate: TranslateService) {
  }

  public parseStatusEventDetailsToHtmlTable(details: Map<string, StatusEventDetails>): string {
    let message: string = '';
    if (!details || details.size == 0) {
      message = this.translate.instant('The source to parse to HTML is invalid');
      throw new StatusEventSignalParsingException(StatusEventExceptionTypes.DOWNLOAD_INVALID_SOURCE, message);
    }

    const firstEntry: StatusEventDetails = Array.from(details.values())[0];

    const languageKeys = this.getSortedKeysArray(firstEntry.localizations);
    const metadataKeys = this.getSortedKeysArray(firstEntry.metadata);

    const headerRow = this.generateHeaderRow(metadataKeys, languageKeys);
    const content = this.generateContentBody(details, metadataKeys, languageKeys);

    return this.generateTable(headerRow + content);

  }

  private getSortedKeysArray(map: Map<string, string>): string[] {
    if (!map || map.size === 0) {
      return [];
    }

    const keys: string[] = Array.from(map.keys());
    return keys.sort();
  }


  private generateHeaderRow(metadataKeys: string[], languageKeys: string[]): string {
    const result: string[] = [];
    result.push(this.generateTableHeaderCell(StatusEventSignalColumnKind.CODE));
    result.push(this.generateTableHeaderCell(StatusEventSignalColumnKind.TYPE));
    result.push(this.generateTableHeaderCell(StatusEventSignalColumnKind.DESCRIPTION));

    for (const metadata of metadataKeys) {
      result.push(this.generateTableHeaderCell(metadata));
    }
    for (const language of languageKeys) {
      result.push(this.generateTableHeaderCell(STATUS_EVENT_SIGNAL_LANGUAGE_COLUMN_PREFIX + language));
    }
    return this.generateTableRow(result.join(''));

  }

  private generateContentBody(details: Map<string, StatusEventDetails>, metadataKeys: string[], languageKeys: string[]): string {
    let result: string = '';

    details.forEach((item: StatusEventDetails) => {
      result += this.generateTableRow(this.generateContentRow(item, metadataKeys, languageKeys));
    });

    return result;
  }

  private generateContentRow(event: StatusEventDetails, metadataKeys: string[], languageKeys: string[]): string {
    const result: string[] = [];
    result.push(this.generateTableBodyCell(event.code));
    result.push(this.generateTableBodyCell(event.type));
    result.push(this.generateTableBodyCell(event.description));

    for (const metadata of metadataKeys) {
      let value = event.metadata.get(metadata);
      if (!value) {
        value = '';
      }
      result.push(this.generateTableBodyCell(value));
    }
    for (const language of languageKeys) {
      let value = event.localizations.get(language);
      if (!value) {
        value = '';
      }
      result.push(this.generateTableBodyCell(value));
    }

    return result.join('');
  }


  private generateTableHeaderCell(value: string) {
    return this.TABLE_HEADER_START + value + this.TABLE_HEADER_END;
  }

  private generateTableBodyCell(value: string) {
    return this.TABLE_CELL_START + value + this.TABLE_CELL_END;
  }

  private generateTableRow(value: string) {
    return this.TABLE_ROW_START + value + this.TABLE_ROW_END;
  }

  private generateTable(value: string) {
    return this.TABLE_TAG_START + value + this.TABLE_TAG_END;
  }
}
